import React, { memo, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { TimesheetPeriodNavigator } from '../timesheetPeriod';
import { SmartDiaryNotesSkeleton } from './SmartDiaryNotesSkeleton';
import { SmartDiaryContent } from './SmartDiaryContent';
import {
  useSmartDiaryEntries,
  LoadMorePeriodType,
  useUpdateExpandCollapseOnInitialLoad
} from './hooks';
import { SmartDiaryViewOptionsSelector } from './SmartDiaryViewOptionsSelector';

type SmartDiaryNotesProps = {
  loadMorePeriods: LoadMorePeriodType;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    marginRight: theme.spacing(0)
  },
  viewOptions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: theme.spacing(0, 1, 0, 2)
  }
}));

export const SmartDiaryNotes: React.FC<SmartDiaryNotesProps> = memo(
  ({ loadMorePeriods }: SmartDiaryNotesProps) => {
    const classes = useStyles();
    const { loading, smartEntriesByDay, lastSyncTime } = useSmartDiaryEntries();
    const content = useMemo(() => {
      if (loading || !smartEntriesByDay) {
        return <SmartDiaryNotesSkeleton />;
      }
      return (
        <SmartDiaryContent
          smartEntriesByDay={smartEntriesByDay}
          lastSyncTime={lastSyncTime}
        />
      );
    }, [lastSyncTime, loading, smartEntriesByDay]);

    useUpdateExpandCollapseOnInitialLoad({
      loading,
      smartEntriesByDay
    });

    return (
      <div className={classes.root} data-testid="smart-diary-notes-container">
        <div
          className={classes.viewOptions}
          data-testid="smart-diary-date-navigator-container"
        >
          <TimesheetPeriodNavigator loadMorePeriods={loadMorePeriods} />
          <SmartDiaryViewOptionsSelector />
        </div>
        {content}
      </div>
    );
  }
);

SmartDiaryNotes.displayName = 'SmartDiaryNotes';
